class UpsellOptions {
  constructor() {
    this.upsellTotal = 0;
    this.selectedUpsells = new Map();
    this.basePrice = 0;
    this.basePriceFormatted = '';
    this.initialized = false;
    this.isProcessingVariantChange = false;
    this.variantChangeUnsubscriber = null;
    this.handleUpsellChangeBound = this.handleUpsellChange.bind(this);
    this.init();
  }

  init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    if (this.initialized) return;
    this.bindUpsellInputs();
    this.storeBasePrice();
    this.applyPreselectedUpsells();
    this.subscribeToVariantChanges();
    this.initialized = true;
  }

  applyPreselectedUpsells() {
    const upsellInputs = document.querySelectorAll('.upsell-options input[type="radio"]:checked');
    upsellInputs.forEach(input => {
      const value = input.value;
      const price = input.dataset.price ? parseInt(input.dataset.price, 10) : 0;
      if (value !== 'none' && price > 0) {
        this.selectedUpsells.set(input.name, price);
      }
    });
    if (this.selectedUpsells.size > 0) {
      this.calculateUpsellTotal();
      this.updateDisplayedPrice();
    }
  }

  bindUpsellInputs() {
    const upsellInputs = document.querySelectorAll('.upsell-options input[type="radio"]');
    upsellInputs.forEach(input => {
      input.removeEventListener('change', this.handleUpsellChangeBound);
      input.addEventListener('change', this.handleUpsellChangeBound);
    });
  }

  storeBasePrice() {
    const priceElement = this.getPriceElement();
    if (priceElement) {
      const priceItemSale = priceElement.querySelector('.price-item--sale');
      const priceItemRegular = priceElement.querySelector('.price__regular .price-item--regular');
      
      const activePriceElement = priceElement.classList.contains('price--on-sale') 
        ? priceItemSale 
        : priceItemRegular;
      
      if (activePriceElement) {
        // If a cached base price exists on the element, use it — prevents reading
        // an already-inflated price on reinit calls after upsells have been applied.
        if (activePriceElement.dataset.basePrice) {
          this.basePrice = parseInt(activePriceElement.dataset.basePrice, 10);
          this.basePriceFormatted = activePriceElement.dataset.basePriceFormatted || activePriceElement.textContent.trim();
        } else {
          this.basePriceFormatted = activePriceElement.textContent.trim();
          this.basePrice = this.parsePriceFromText(this.basePriceFormatted);
          activePriceElement.dataset.basePrice = this.basePrice;
          activePriceElement.dataset.basePriceFormatted = this.basePriceFormatted;
        }
      }
    }
  }

  getPriceElement() {
    return document.querySelector('[id^="price-"] .price') || 
        document.querySelector('.product__price .price') ||
        document.querySelector('.price');
  }

  parsePriceFromText(priceText) {
    let cleanedText = priceText.replace(/^(from|ab|depuis|da)\s*/i, '').trim();
    
    const numericMatch = cleanedText.match(/[\d.,]+/);
    if (!numericMatch) return 0;
    
    let cleanedPrice = numericMatch[0]
      .replace(/,(?=\d{3})/g, '')
      .replace(/\.(?=\d{3})/g, '')
      .replace(/,/g, '.');
    return Math.round(parseFloat(cleanedPrice) * 100) || 0;
  }

  handleUpsellChange(event) {
    const input = event.target;
    const upsellGroupName = input.name;
    const price = input.dataset.price ? parseInt(input.dataset.price, 10) : 0;
    const value = input.value;

    if (value === 'none' || price === 0) {
      this.selectedUpsells.delete(upsellGroupName);
    } else {
      this.selectedUpsells.set(upsellGroupName, price);
    }

    this.updateSelectedOptionTitle(input);
    this.calculateUpsellTotal();
    this.updateDisplayedPrice();
  }

  updateSelectedOptionTitle(input) {
    const groupName = input.name;
    const groupContainer = input.closest('.upsell-options') || document;
    const titleEl = groupContainer.querySelector(`[data-upsell-group="${groupName}"] .selected-option-title`) || groupContainer.querySelector('.selected-option-title');
    const title = input.dataset.title || input.dataset.optionTitle || input.value;
    if (!titleEl) return;
    
    if (title === 'none') {
      titleEl.textContent = '';
    } else {
      titleEl.textContent = ` - ${title}`;
    }
  }

  calculateUpsellTotal() {
    this.upsellTotal = 0;
    this.selectedUpsells.forEach((price) => {
      this.upsellTotal += price;
    });
  }

  updateDisplayedPrice() {
    const priceElement = this.getPriceElement();
    if (!priceElement) return;

    const totalPrice = this.basePrice + this.upsellTotal;
    const formattedTotal = this.formatPrice(totalPrice);
    document.querySelectorAll('[data-bottom-product-price]').forEach(element => {
  element.textContent = formattedTotal;
});
    const priceItemSale = priceElement.querySelector('.price-item--sale');
    const priceItemRegular = priceElement.querySelector('.price__regular .price-item--regular');

    if (priceElement.classList.contains('price--on-sale') && priceItemSale) {
      priceItemSale.textContent = formattedTotal;
    } else if (priceItemRegular) {
      priceItemRegular.textContent = formattedTotal;
    }
  }

  formatPrice(priceInCents) {
    const price = priceInCents / 100;
    
    if (typeof theme !== 'undefined' && theme.settings && theme.settings.moneyWithCurrencyFormat) {
      return this.formatWithShopifyFormat(priceInCents, theme.settings.moneyWithCurrencyFormat);
    }
    
    return this.formatFallback(price);
  }

  formatWithShopifyFormat(priceInCents, format) {
    const price = priceInCents / 100;
    
    let formattedPrice = format
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, this.numberWithCommas(price.toFixed(2).replace('.', ',')))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, this.numberWithCommas(Math.round(price).toString().replace(/\./g, ',')))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(price).toString())
      .replace(/\{\{\s*amount\s*\}\}/g, price.toFixed(2))
      .replace(' USD', '');
    
    return formattedPrice;
  }

  numberWithCommas(x) {
    const parts = x.toString().split(/[.,]/);
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  formatFallback(price) {
    const hasLeadingCurrency = /^[^\d]/.test(this.basePriceFormatted);
    const hasFromPrefix = /^from/i.test(this.basePriceFormatted);
    const currencyMatch = this.basePriceFormatted.match(/[^\d\s.,]+/);
    const currencySymbol = currencyMatch ? currencyMatch[0] : '$';
    const formattedNumber = price.toFixed(2);
    
    if (this.basePriceFormatted.includes(',') && !this.basePriceFormatted.match(/,\d{3}/)) {
      const euroFormat = formattedNumber.replace('.', ',');
      return hasLeadingCurrency 
        ? `${currencySymbol}${euroFormat}` 
        : `${euroFormat} ${currencySymbol}`;
    }

    if (hasFromPrefix) {
      return hasLeadingCurrency 
        ? `From ${currencySymbol}${formattedNumber}` 
        : `From ${formattedNumber} ${currencySymbol}`;
    }
    
    return hasLeadingCurrency 
      ? `${currencySymbol}${formattedNumber}` 
      : `${formattedNumber} ${currencySymbol}`;
  }

  subscribeToVariantChanges() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
      this.variantChangeUnsubscriber = null;
    }
    
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (data) => {
        this.handleVariantChange(data);
      });
    }
  }

  handleVariantChange(data) {
    if (this.isProcessingVariantChange) return;
    this.isProcessingVariantChange = true;
    
    setTimeout(() => {
      this.clearBasePriceCache();
      this.storeBasePrice();
    this.updateDisplayedPrice();
      this.isProcessingVariantChange = false;
    }, 200);
  }

  clearBasePriceCache() {
    const priceElement = this.getPriceElement();
    if (!priceElement) return;
    const els = priceElement.querySelectorAll('[data-base-price]');
    els.forEach(el => {
      delete el.dataset.basePrice;
      delete el.dataset.basePriceFormatted;
    });
  }

  reinit() {
    this.bindUpsellInputs();
    this.isProcessingVariantChange = false;
    this.storeBasePrice();
    if (this.upsellTotal > 0) {
      this.updateDisplayedPrice();
    }
  }
}

if (!window._UpsellOptionsInitialized) {
  window._UpsellOptionsInitialized = true;
  
  function initUpsellOptions() {
    if (document.querySelector('.upsell-options')) {
      if (!window.UpsellOptions) {
        window.UpsellOptions = new UpsellOptions();
      } else {
        window.UpsellOptions.reinit();
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsellOptions);
  } else {
    initUpsellOptions();
  }

  document.addEventListener('product-info:loaded', () => {
    if (window.UpsellOptions) {
      window.UpsellOptions.reinit();
    }
  });
}
